import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import OneXButton from "../OneX/OneXButton";
import FormValidator from "../Utility/FormValidator";
import OneXTextField from "../OneX/OneXTextField";
import OneXPageLevelNotification from "../OneX/OneXPageLevelNotification";
import OneXPageLoader from "../OneX/OneXPageLoader";
import OneXSpecs from "../OneX/OneXSpecs";
// eslint-disable-next-line
import { postMatch } from "../Utility/API";
import styled from "styled-components";
import EnsightenAnalytics from "../Utility/EnsightenAnalytics";
import Flex from "../LAUI/Flex";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  firstNameState,
  lastNameState,
  birthdateState,
  emailState,
  waitingForResponseState,
  submitAttemptCountState,
  pageLevelErrorState,
  successfulMatchState,
} from "../AppData/Recoil";
import { useMedia } from "use-media";

const SpacingDiv = styled.div`
  margin-bottom: 48px;
`;

const TextFieldSpacing = styled.div`
  margin-bottom: 16px;
  margin-left: 1px;
`;

const GetSavedQuotes = ({ id }) => {
  const [firstName, setFirstName] = useRecoilState(firstNameState);
  const [lastName, setLastName] = useRecoilState(lastNameState);

  const [birthdate, setBirthdate] = useRecoilState(birthdateState);
  const [email, setEmail] = useRecoilState(emailState);
  const [submitAttemptCount, setSubmitAttemptCount] = useRecoilState(submitAttemptCountState);
  const [pageLevelError, setPageLevelError] = useRecoilState(pageLevelErrorState);
  const [waitingForResponse, setWaitingForResponse] = useRecoilState(waitingForResponseState);
  const setSuccesfulMatch = useSetRecoilState(successfulMatchState);

  const [init, setInit] = useState(false);

  const formRef = useRef(null);

  const isMobile = useMedia(OneXSpecs.DEVICE_MEDIA_QUERIES.MOBILE);
  const isTablet = useMedia(OneXSpecs.DEVICE_MEDIA_QUERIES.TABLET);
  const isDesktop = useMedia(OneXSpecs.DEVICE_MEDIA_QUERIES.DESKTOP);

  const handleEnterKey = (event) => {
    if (event.key === "Enter") {
      handleClick();
    }
  };

  const errorHandler = (error) => {
    console.error(error);
    setPageLevelError(0);
    setWaitingForResponse(false);
  }

  // eslint-disable-next-line
  const handleClick = () => {
    postMatch(
      firstName,
      lastName,
      birthdate,
      email,
      setWaitingForResponse,
      setSubmitAttemptCount,
      submitAttemptCount,
      setPageLevelError,
      setSuccesfulMatch,
      errorHandler
    );
  };

  useEffect(() => {
    if (!init) {
      window.scrollTo(0, 0);
      setInit(true);
      EnsightenAnalytics.pushScreenChangeToAnalytics("saved-quotes");
    } else {
    }
    // eslint-disable-next-line
  }, [init]);

  return !waitingForResponse ? (
    <div
      id={id}
      className={"-oneX-col-lg-7 -oneX-col-md-9 -oneX-col-sm-10"}
      style={{
        marginBottom: "224px"
      }}
      ref={formRef}
      onKeyPress={handleEnterKey}
    >
      <h1 id={`${id}-header`} style={{ marginTop: isDesktop ? "56px" : isTablet ? "48px" : isMobile ? "40px" : "40px", marginBottom: "63px" }}>
        {"Review your saved quotes"}
      </h1>
      {pageLevelError > -1 ? (
        <div class="-oneX-notification"
          style={{ marginBottom: "57px" }}
          role="alert"
          aria-label="Error"
          aria-live="assertive"
          tabindex="-1"
        >
          <OneXPageLevelNotification id={"PLN_Error"} type={"Error"}>
            {pageLevelError === 501 ? "We couldn't find a match with the information entered. Call us at 888-559-1922 and we should be able to help." : submitAttemptCount < 5
              ? "We couldn't find a match with the information entered.  Please try again."
              : "We couldn't find a match with the information entered.  Please contact your agent."}
          </OneXPageLevelNotification>
        </div>
      ) : null}
      <div className={"-oneX-body--primary"} style={{ marginBottom: "27px" }}>
        Answer a few questions so we can locate your quotes.  From there, you can view details and pick up where you left off.
      </div>
      <div className="-oneX-row">
        <OneXTextField
          id={"firstNameTextField"}
          label={"First name"}
          defaultValue={firstName.value}
          required={true}
          lg_col={5}
          errorCode={firstName.validationCode}
          changeHandler={(e) => setFirstName({ value: e.target.value, validationCode: firstName.validationCode })}
          blurHandler={(e) => setFirstName({ value: e.target.value, validationCode: FormValidator.validateRequiredField(e.target.value) })}
          errors={["Please enter a valid first name."]}
        />
        <TextFieldSpacing />
        <OneXTextField
          id={"lastNameTextField"}
          label={"Last name"}
          defaultValue={lastName.value}
          required={true}
          lg_col={5}
          errorCode={lastName.validationCode}
          changeHandler={(e) => setLastName({ value: e.target.value, validationCode: lastName.validationCode })}
          blurHandler={(e) => setLastName({ value: e.target.value, validationCode: FormValidator.validateRequiredField(e.target.value) })}
          errors={["Please enter a valid last name."]}
        />
      </div>
      <TextFieldSpacing />
      <div className="-oneX-row">
        <OneXTextField
          id={"birthdateTextField"}
          label={"Date of birth (MM-DD-YYYY)"}
          type="tel"
          defaultValue={birthdate.value}
          required={true}
          lg_col={5}
          errorCode={birthdate.validationCode}
          dataFormatter={"date"}
          errors={["Please enter a valid date of birth."]}
          changeHandler={(e) => setBirthdate({ value: e.target.value, validationCode: FormValidator.validateRequiredField(e.target.value)})}
          blurHandler={(e) => setBirthdate({ value: e.target.value, validationCode: FormValidator.validateDateOfBirthField(e.target.value) })}
          maskTeaLeaf={true}
        />
      </div>
      <TextFieldSpacing />
      <div className="-oneX-row">
        <OneXTextField
          id={"emailTextField"}
          label={"Email address"}
          required={true}
          lg_col={10}
          errorCode={email.validationCode}
          defaultValue={email.value}
          errors={["Please enter a valid email address."]}
          changeHandler={(e) => setEmail({ value: e.target.value, validationCode: FormValidator.validateRequiredField(e.target.value) })}
          blurHandler={(e) => setEmail({ value: e.target.value, validationCode: FormValidator.validateEmailField(e.target.value) })}
          maskTeaLeaf={true}
        />
      </div>

      <SpacingDiv />
      <OneXButton
        id={`${id}-primary-button`}
        oneXType={"primary"}
        clickHandler={() => {
          handleClick();
        }}
        disabled={submitAttemptCount >= 5}
      >
        {"Find quotes"}
      </OneXButton>
    </div>
  ) : (
    <Flex style={{ marginTop: "105px", marginBottom: "105px" }}>
      <OneXPageLoader />
    </Flex>
  );
};

export default GetSavedQuotes;

GetSavedQuotes.propTypes = {
  header: PropTypes.string,
  id: PropTypes.string.isRequired,
  primaryText: PropTypes.string,
  secondaryText: PropTypes.string,
  primaryRoute: PropTypes.string,
  secondaryRoute: PropTypes.string,
  children: PropTypes.element,
};
